import * as R from 'ramda';

import { HttpService } from '~/services';
import {
  getRouteNavigationParamsWithFormattedDate,
  renameKeys,
} from '~/helpers';
import { INTERNAL_TRX_FEE, INTERNAL_TRX_LIST } from '~/gql';

export const resolvers = {
  Query: {
    internalTransactionsList: async (_, props, { cache }) => {
      const columnsMapping = {
        DateTimeUtc: 'date',
        Username: 'username',
        Status: 'status',
        TrxId: 'trxId',
        TrxType: 'trxType',
        AmountMbtc: 'amountMbtc',
      };

      const params = getRouteNavigationParamsWithFormattedDate(
        {
          date: ['DateFromUtc', 'DateToUtc'],
          username: 'Username',
          trxId: ['TrxIdFrom', 'TrxIdTo'],
          trxType: 'TrxType',
          status: 'Status',
          amountMbtc: ['AmountMbtcFrom', 'AmountMbtcTo'],
        },
        R.invertObj(columnsMapping),
      )(props);

      const {
        data: {
          TrxList,
          PageCount,
          TotalSent,
          TotalReceived,
          AvailableBalance,
        },
      } = await HttpService.client.get(
        '/MyWealth/InternalTransfer/GetTrxList',
        {
          params,
        },
      );

      const items = R.map(
        R.pipe(
          renameKeys(columnsMapping),
          R.evolve({
            amountMbtc: parseFloat,
          }),
          R.assoc('__typename', 'InternalTransactionsItem'),
          (item) => {
            item.id = item.trxId;
            return item;
          },
        ),
        TrxList,
      );

      const internalTransactionsList = {
        data: items,
        pages: PageCount,
        totalSent: parseFloat(TotalSent),
        totalReceived: parseFloat(TotalReceived),
        availableBalance: parseFloat(AvailableBalance),
        __typename: 'InternalTransactionsList',
      };

      cache.writeQuery({
        query: INTERNAL_TRX_LIST,
        data: { internalTransactionsList },
        variables: props,
      });

      return internalTransactionsList;
    },

    internalTransactionTypeList: async (_, __, { cache }) => {
      const {
        data: { TrxType },
      } = await HttpService.client.get('/MyWealth/InternalTransfer/GetTrxType');

      const list = R.map(
        R.pipe(
          renameKeys({
            Value: 'value',
            Name: 'name',
          }),
          R.assoc('__typename', 'TrxTypeList'),
        ),
        TrxType,
      );

      cache.writeData({
        data: { list, __typename: 'InternalTransactionTypeList' },
      });

      return list;
    },

    internalTransactionStatusTypeList: async (_, __, { cache }) => {
      const {
        data: { StatusType },
      } = await HttpService.client.get(
        '/MyWealth/InternalTransfer/GetStatusType',
      );

      const list = R.map(
        R.pipe(
          renameKeys({
            Value: 'value',
            Name: 'name',
          }),
          R.assoc('__typename', 'TrxStatusTypeList'),
        ),
        StatusType,
      );

      cache.writeData({
        data: { list, __typename: 'InternalTransactionStatusTypeList' },
      });

      return list;
    },

    internalTransactionFee: async (_, { amount }, { cache }) => {
      const {
        data: { Fee },
      } = await HttpService.client.get('/MyWealth/InternalTransfer/GetTrxFee', {
        params: { Amount: amount },
      });

      const fee = {
        fee: parseFloat(Fee),
        __typename: 'InternalTransactionFee',
      };

      cache.writeQuery({
        query: INTERNAL_TRX_FEE,
        data: fee,
      });

      return fee;
    },
  },
  Mutation: {
    internalTransactionSubmitMutation: async (_, { values }) => {
      const params = renameKeys({
        amount: 'Amount',
        idToken: 'IdToken',
        username: 'Username',
      })(R.pick(['amount', 'idToken', 'username'], values));

      try {
        const {
          data: { ReturnCode, ReturnMessage },
        } = await HttpService.client.post(
          '/MyWealth/InternalTransfer/Submit',
          params,
        );

        return {
          code: ReturnCode,
          message: ReturnMessage,
          __typename: 'ReturnType',
        };
      } catch (e) {
        return {
          code: e.code,
          message: e.message,
          __typename: 'ReturnType',
        };
      }
    },
  },
};
